// Module: reconcileStates.js
// Purpose: Reconcile-state constants and helpers for the consolidated-orders view (R104.5)

/*>>> RECONCILE STATE DEFINITIONS <<<*/

/**
 * RECONCILE_STATE_TOOLTIPS — canonical per-state badge and tooltip copy.
 * QA asserts the shortTooltip and expandedTooltip strings character-for-character.
 * Do NOT edit these strings without updating the test fixtures.
 */
export const RECONCILE_STATE_TOOLTIPS = {
  matched: {
    badge: '✓',
    //! colorClass — visually distinct from broker_only (green vs amber)
    colorClass: 'bg-green-500/15 text-green-400 border-green-500/20',
    variant: 'profit',
    shortTooltip: 'DeeTrade and the broker agree.',
    expandedTooltip:
      "DeeTrade's record for this item matches the broker's book (id, side, qty, status) as of the last reconcile. Highest confidence.",
  },

  deetrade_only: {
    badge: '⚠',
    colorClass: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    variant: 'warn',
    shortTooltip: "DeeTrade has this; the broker book doesn't (yet).",
    expandedTooltip:
      "DeeTrade recorded this but the broker's book didn't return it at the last reconcile — usually a just-placed/working order the broker hasn't echoed, or an order rejected broker-side. Check again after the next reconcile.",
  },

  broker_only: {
    badge: '⚠',
    //! colorClass — MUST be visually distinct from matched (orange, not green) per R104.5
    colorClass: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
    variant: 'warn',
    shortTooltip: "The broker has this; DeeTrade never saw it.",
    expandedTooltip:
      "The broker's book shows this order/trade/position but DeeTrade has no record — typically a trade placed directly at the broker (web/app) that never came through our feed. This is real activity on your account that DeeTrade is not managing.",
  },

  state_mismatch: {
    badge: '⚠',
    colorClass: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    variant: 'warn',
    shortTooltip: 'Both have it, but details differ.',
    expandedTooltip:
      'Both DeeTrade and the broker show this item but a field disagrees (qty, price, or status) beyond tolerance, and it is not explained by a normal partial-fill or amend. Worth a look.',
  },

  pending_too_fresh: {
    badge: '◷',
    colorClass: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    variant: 'info',
    shortTooltip: 'Too recent to reconcile.',
    expandedTooltip:
      'Placed within the grace window; the broker book may simply not have caught up. Not a discrepancy — recheck after the next reconcile.',
  },

  partial_fill: {
    badge: '▱',
    colorClass: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    variant: 'info',
    shortTooltip: 'Working order, partially filled — normal.',
    expandedTooltip:
      'DeeTrade and the broker agree this order is still working with a partial fill. The qty difference is expected, not drift.',
  },

  replaced_amended: {
    badge: '⟳',
    colorClass: 'bg-gray-500/15 text-fg-3 border-[var(--divider)]',
    variant: 'neutral',
    shortTooltip: 'Part of a cancel-replace / amend chain.',
    expandedTooltip:
      'This order was modified or replaced; DeeTrade has linked the original and the replacement as one logical order. Shown as one chain, not two phantoms.',
  },

  not_reconciled_outside_window: {
    badge: '∅',
    colorClass: 'bg-gray-500/15 text-fg-3 border-[var(--divider)]',
    variant: 'neutral',
    shortTooltip: 'Shown from DeeTrade records — not cross-checked with the broker.',
    expandedTooltip:
      "This date is outside the broker book's reconcilable window (the broker book is day-scoped), so we could not compare against broker truth. Absence of a discrepancy flag here does NOT mean 'matched.'",
  },

  //! algo_only — amber/orange: ALGO order present in DeeTrade but no matching broker book entry.
  //! NOT red/error — this is a "needs review" state, not a hard failure. Distinct from broker_only
  //! (which is broker-book-only) and from matched (green). Visual: amber flag glyph.
  algo_only: {
    badge: '⚑',
    colorClass: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    variant: 'warn',
    shortTooltip: 'DeeTrade algo order not yet confirmed by the broker.',
    expandedTooltip:
      'DeeTrade placed this algo order but the broker book has no matching entry yet. It may be in-flight or pending acknowledgement. Non-destructive — reconcile again in a few seconds.',
  },

  //! pending_sweep — grey/blue: order is too recent or in a pending state awaiting broker confirmation.
  //! Distinct clock glyph, distinct colour from algo_only (which is amber flag).
  pending_sweep: {
    badge: '◷',
    colorClass: 'bg-sky-500/15 text-sky-400 border-sky-500/20',
    variant: 'info',
    shortTooltip: 'Awaiting broker confirmation — pending sweep.',
    expandedTooltip:
      'This order is in a pending state and has not yet been swept into the broker book. Reconcile state will resolve once the broker confirms or rejects the order. No action required yet.',
  },

  reconcile_unavailable: {
    badge: '✕',
    colorClass: 'bg-red-500/15 text-red-400 border-red-500/20',
    variant: 'loss',
    shortTooltip: "Couldn't reach the broker to reconcile.",
    expandedTooltip:
      "The broker reconcile fetch failed or was rate-limited for this account, so reconcile state is unknown right now. Try 'Reconcile now' shortly. (We never show un-reconciled data as if it matched.)",
  },
};

/*>>> HELPER <<<*/

/**
 * getReconcileState — returns the RECONCILE_STATE_TOOLTIPS entry for `state`.
 * R104.5 AC(d): missing / unknown state defaults to reconcile_unavailable, NOT blank.
 * R104.11: null state returns null so ReconcileBadge can suppress rendering entirely
 *   (positions / unclassified rows must NOT show any badge when state is null).
 *
 * @param {string|null|undefined} state
 * @returns {{ badge: string, colorClass: string, variant: string, shortTooltip: string, expandedTooltip: string } | null}
 */
export function getReconcileState(state) {
  //! R104.11 — null/undefined means "no reconcile state set" (e.g. positions, unclassified).
  //  Return null so the caller can decide to render nothing.
  if (state == null) {
    return null;
  }
  if (RECONCILE_STATE_TOOLTIPS[state]) {
    return RECONCILE_STATE_TOOLTIPS[state];
  }
  // Unknown string state → default to reconcile_unavailable (honest fallback)
  return RECONCILE_STATE_TOOLTIPS.reconcile_unavailable;
}
